<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import ApiService from "@/services/jobsiteTrackingService";
import ProductHelper from "@/utils/productHelper";
import Multiselect from "vue-multiselect";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import AlertModal from "@/components/modals/alertModal";
import ConfirmCompletedModal from "@/components/modals/confirmCompletedModal";
import SignatureModal from "@/components/modals/signatureModal";
import BarCodeReader from "@/components/barCodeReader";
import FilesModal from "@/components/modals/filesModal";
import UserConfigMixin from "@/mixins/userConfigMixin";

import JobInfoTab from "./v2/JobInfoTab";
import ProgressionTab from "./v2/ProgressionTab";
import ItemsTab from "./v2/ItemsTab";
import NotesTab from "./v2/NotesTab";
import ActionButtons from "./v2/ActionButtons";

export default {
  mixins: [UserConfigMixin],
  page: {
    title: "Items",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    Multiselect,
    Loading,
    AlertModal,
    ConfirmCompletedModal,
    SignatureModal,
    BarCodeReader,
    FilesModal,
    JobInfoTab,
    ProgressionTab,
    ItemsTab,
    NotesTab,
    ActionButtons,
  },
  data() {
    return {
      queryResult: {
        products: [],
        productBarcodeGetResources: [],
        workOrderSteps: [],
        availablePackagings: [],
        availableWorkOrders: [],
        workOrderInfo: null,
      },
      packingFilter: null,
      workOrderFilter: null,
      steps: [],
      currentStep: null,
      reasons: [],
      loaded: false,
      isLoading: false,
      isAlertsLoading: false,
      tabIndex: 2,
      alertMode: false,
    };
  },
  computed: {
    headerTitle() {
      if (this.workOrderFilter) return "JOB: " + this.workOrderFilter.name;
      if (this.packingFilter) return this.packingFilter.name;
      return this.$t("general.all");
    },
    nbBO() {
      return this.queryResult.products.filter((x) => x.isBackOrder).length;
    },
    nbDamaged() {
      return this.queryResult.products.filter((x) => x.isDamaged).length;
    },
    nbAlerts() {
      return this.nbBO + this.nbDamaged;
    },
    nbProduct() {
      return this.queryResult.products.filter((x) => !x.isDetached).length;
    },
    nbDetached() {
      return this.queryResult.products.filter((x) => x.isDetached).length;
    },
    nbProductCompleted() {
      if (!this.currentStep) return 0;
      const products = this.queryResult.products.filter(
        (x) =>
          !x.isDetached &&
          (ProductHelper.isProductStepCompleted(x, this.currentStep) ||
            ProductHelper.hasNoProductStep(x, this.currentStep) ||
            ProductHelper.isProductDamaged(x) ||
            ProductHelper.isProductBackOrder(x)),
      );
      return products.length;
    },
    nbDetachedCompleted() {
      if (!this.currentStep) return 0;
      return this.queryResult.products.filter(
        (x) =>
          x.isDetached &&
          (ProductHelper.isProductStepCompleted(x, this.currentStep) ||
            ProductHelper.hasNoProductStep(x, this.currentStep) ||
            ProductHelper.isProductDamaged(x) ||
            ProductHelper.isProductBackOrder(x)),
      ).length;
    },
    completedCount() {
      return this.nbProductCompleted + this.nbDetachedCompleted;
    },
    totalCount() {
      return this.nbProduct + this.nbDetached;
    },
    percentCompleted() {
      if (this.totalCount === 0) return 0;
      return Math.round((100 * this.completedCount) / this.totalCount);
    },
    currentStepIdPublic() {
      return this.currentStep?.idPublic ?? null;
    },
    currentWorkOrderStepIdPublic() {
      if (!this.workOrderFilter || !this.currentStep) return null;
      const match = this.queryResult.workOrderSteps.find(
        (wos) =>
          wos.workOrderIdPublic === this.workOrderFilter.idPublic &&
          wos.step.idPublic === this.currentStep?.idPublic,
      );
      return match?.idPublic ?? null;
    },
    workOrderOptions() {
      return [
        { idPublic: null, name: this.$t("general.all") },
        ...this.queryResult.availableWorkOrders,
      ];
    },
    eligibleWorkOrderIds() {
      if (!this.currentStep) return [];
      const ids = this.queryResult.workOrderSteps
        .filter(
          (wos) =>
            wos.step.idPublic === this.currentStep?.idPublic &&
            !wos.completedJobsite,
        )
        .map((wos) => wos.workOrderIdPublic);
      const unique = [...new Set(ids)];
      if (this.workOrderFilter) {
        return unique.filter((id) => id === this.workOrderFilter.idPublic);
      }
      return unique;
    },
    currentWorkOrderStepCompleted() {
      return this.eligibleWorkOrderIds.length === 0;
    },
    eligibleProducts() {
      if (!this.currentStep) return [];
      return this.queryResult.products.filter(
        (x) =>
          !ProductHelper.isProductStepCompleted(x, this.currentStep) &&
          !(
            ProductHelper.hasNoProductStep(x, this.currentStep) ||
            ProductHelper.isProductDamaged(x) ||
            ProductHelper.isProductBackOrder(x)
          ),
      );
    },
    routePackingId() {
      if (this.$route.name === "packingProducts") {
        return this.$route.params.packingId || null;
      }
      return this.$route.query.packagingPublicId || null;
    },
    routeWorkOrderId() {
      if (this.$route.name === "workOrderProducts") {
        return this.$route.params.workOrderPublicId || null;
      }
      return this.$route.query.workOrderPublicId || null;
    },
    routeKey() {
      return (this.routePackingId || "") + "|" + (this.routeWorkOrderId || "");
    },
    packingOptions() {
      return [
        { idPublic: null, name: this.$t("general.all") },
        ...this.queryResult.availablePackagings,
      ];
    },
    allProductCompleted() {
      return (
        this.currentStep == undefined ||
        this.queryResult.products.filter(
          (x) =>
            !ProductHelper.isProductStepCompleted(x, this.currentStep) &&
            !(
              ProductHelper.hasNoProductStep(x, this.currentStep) ||
              ProductHelper.isProductDamaged(x) ||
              ProductHelper.isProductBackOrder(x)
            ),
        ).length == 0
      );
    },
    showFilesButton() {
      return Boolean(this.queryResult.workOrderInfo)
    },
    canUndoComplete() {
      return this.userIsAdmin
    }
  },
  watch: {
    routeKey() {
      this.getData();
    },
    packingFilter(val) {
      const next = val?.idPublic || null;
      if (next === this.routePackingId) return;
      this.updateUrl(next, this.routeWorkOrderId);
    },
    workOrderFilter(val) {
      const next = val?.idPublic || null;
      if (next === this.routeWorkOrderId) return;
      this.updateUrl(this.routePackingId, next);
    },
  },
  methods: {
    optionIconClass(option) {
      return option?.idPublic == null
        ? "mdi-format-list-bulleted"
        : "mdi-briefcase-outline";
    },
    updateUrl(packingId, workOrderId) {
      const routeName = this.$route.name;

      if (routeName === "packingProducts") {
        if (!packingId) {
          this.$router.replace(
            workOrderId
              ? {
                  name: "workOrderProducts",
                  params: { workOrderPublicId: workOrderId },
                }
              : { name: "productsByPacking" },
          );
          return;
        }
        this.$router.replace({
          name: "packingProducts",
          params: { packingId },
          query: workOrderId ? { workOrderPublicId: workOrderId } : {},
        });
        return;
      }

      if (routeName === "workOrderProducts") {
        if (!workOrderId) {
          this.$router.replace(
            packingId
              ? {
                  name: "packingProducts",
                  params: { packingId },
                }
              : { name: "productsByPacking" },
          );
          return;
        }
        this.$router.replace({
          name: "workOrderProducts",
          params: { workOrderPublicId: workOrderId },
          query: packingId ? { packagingPublicId: packingId } : {},
        });
        return;
      }

      const query = {};
      if (packingId) query.packagingPublicId = packingId;
      if (workOrderId) query.workOrderPublicId = workOrderId;
      this.$router.replace({ name: "productsByPacking", query });
    },
    selectPacking(option) {
      this.packingFilter = option;
    },
    toggleAlertMode() {
      if (this.isAlertsLoading) return;
      this.alertMode = !this.alertMode;
    },
    selectStep(step) {
      if (!step) return;
      this.currentStep = step;
      this.$store.dispatch("userconfig/setUserConfig", {
        userId: this.$msal.msalInstance.getAllAccounts()[0].localAccountId,
        defaultStep: step.idPublic,
      });
    },
    async getData() {
      this.isLoading = true;
      this.isAlertsLoading = true;
      try {
        const packingIdPublic = this.routePackingId;
        const workOrderIdPublic = this.routeWorkOrderId;

        const [queryRes, reasonRes] = await Promise.all([
          ApiService.getProductsByQuery({ packingIdPublic, workOrderIdPublic }),
          ApiService.getReasons(),
        ]);
        this.queryResult = queryRes.data.data;
        this.reasons = reasonRes.data.data;

        this.packingFilter =
          this.packingOptions.find((p) => p.idPublic === packingIdPublic) ||
          this.packingOptions[0];
        this.workOrderFilter =
          this.workOrderOptions.find((w) => w.idPublic === workOrderIdPublic) ||
          this.workOrderOptions[0];

        const stepMap = new Map();
        for (const wos of this.queryResult.workOrderSteps) {
          if (!stepMap.has(wos.step.idPublic)) {
            stepMap.set(wos.step.idPublic, wos.step);
          }
        }
        this.steps = [...stepMap.values()];
        const defaultStepId = this.$store.state.userconfig.config.defaultStep;
        this.currentStep =
          this.steps.find((s) => s.idPublic === defaultStepId) ||
          this.steps[0] ||
          null;
        this.loaded = true;
      } catch (error) {
        window.console.error(error);
      } finally {
        this.isLoading = false;
        this.isAlertsLoading = false;
      }
    },
    isStepCompleted(step) {
      return !this.queryResult.workOrderSteps.some(
        (wos) => wos.step.idPublic === step.idPublic && !wos.completedJobsite,
      );
    },
    confirmCompleted(productData) {
      if (!this.currentStep) {
        this.$bvToast.toast(this.$t("general.nosteptocomplete"), {
          title: this.$t("general.nostepavailable"),
          autoHideDelay: 5000,
          appendToast: false,
          variant: "warning",
          solid: true,
        });
        return;
      }
      if (!productData || productData.length === 0) return;
      this.$refs.confirmModal.openEventDialog(productData).then(
        (products) => this.completeProduct(products.map((x) => x.idPublic)),
        () => {},
      );
    },
    undoCompleted(productData) {
      if (!productData || productData.length === 0) return;
      this.$refs.confirmModal.openEventDialog(productData, {
        confirmDialogue: this.$t('general.areyousureundoproductcompleted'),
        confirmBtnText : this.$t('general.markincomplete')
      }).then(
        (products) => this.undoCompleteProduct(products.map((x) => x.idPublic)),
        () => {},
      );
    },
    checkAllProducts() {
      if (this.eligibleProducts.length === 0) return;
      this.confirmCompleted(this.eligibleProducts);
    },
    async completeWorkOrderStep() {
      const targetIds = this.eligibleWorkOrderIds;
      if (targetIds.length === 0 || !this.currentStep) return;
      if (this.stepNeedSignature(this.currentStepIdPublic)) {
        const label =
          targetIds.length === 1 && this.workOrderFilter
            ? this.workOrderFilter.name
            : this.headerTitle;
        try {
          await this.$refs.signatureModal.openEventDialog({
            id: label,
            title: label,
            userCreated: false,
            type: this.currentStep?.name,
          });
        } catch (e) {
          return; // user cancelled signature modal
        }
      }
      this.isLoading = true;
      Promise.all(
        targetIds.map((woId) =>
          ApiService.completeWorkOrderSteps(woId, this.currentStepIdPublic),
        ),
      )
        .then(() => {
          this.getData();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    processProducts(apiEndpoint, idPublics){
      if (!this.currentStep) return Promise.resolve();
      this.isLoading = true;
      return apiEndpoint({
        ProductIdPublics: idPublics,
        StepIdPublic: this.currentStep?.idPublic,
      }).then((res) => {
        for (var index in this.queryResult.products) {
          var productResource = res.data.data.find(
            (x) => x.idPublic == this.queryResult.products[index].idPublic,
          );
          if (productResource) {
            this.replaceProduct(productResource, index);
          }
        }
        this.isLoading = false;
      });
    },
    completeProduct(idPublics) {
      return this.processProducts(ApiService.completeProduct, idPublics)
    },
    undoCompleteProduct(idPublics) {
      return this.processProducts(ApiService.undoCompleteProduct, idPublics)
    },
    replaceProduct(newProduct, index) {
      this.queryResult.products[index] = newProduct;
      this.queryResult.products.__ob__.dep.notify();
    },
    scan(scannedCode) {
      let barcodeitems = this.queryResult.productBarcodeGetResources.filter(
        (x) =>
          x.barcode && x.barcode?.toLowerCase() == scannedCode.toLowerCase(),
      );

      if (barcodeitems.length == 0) {
        barcodeitems = this.queryResult.productBarcodeGetResources.filter(
          (x) =>
            x.rfidBarcode &&
            scannedCode.length >= x.rfidBarcode.length &&
            scannedCode.toLowerCase().endsWith(x.rfidBarcode.toLowerCase()),
        );
      }

      if (barcodeitems.length == 0) {
        this.$bvToast.toast(this.$t("general.unrecognisedscan"), {
          title: this.$t("general.wrongscan"),
          autoHideDelay: 5000,
          appendToast: false,
          variant: "danger",
          solid: true,
        });
        this.$refs.barcodereader.wrongscan();
        return;
      }

      if (this.userConfig.checkWorkOrderOnScan && this.workOrderFilter?.idPublic) {
        const scannedItems = barcodeitems;
        barcodeitems = barcodeitems.filter(
          (x) => x.workOrderIdPublic == this.workOrderFilter.idPublic,
        );
        if (barcodeitems.length == 0) {
          this.$bvToast.toast(this.wrongWorkOrderMessage(scannedItems), {
            title: this.$t("general.wrongscan"),
            autoHideDelay: 10000,
            appendToast: false,
            variant: "danger",
            solid: true,
          });
          this.$refs.barcodereader.wrongjob();
          return;
        }
      }

      var productIdPublics = [...new Set(barcodeitems.map((x) => x.idPublic))];
      var product;
      if (productIdPublics.length == 1) {
        product = this.queryResult.products.find(
          (x) => x.idPublic == productIdPublics[0],
        );
        if (product) {
          if (ProductHelper.hasNoProductStep(product, this.currentStep)) {
            this.$bvToast.toast(
              this.$t("general.stepnotavailable", {
                step: this.currentStep?.name,
                product: product.name,
              }),
              {
                title: this.$t("general.wrongscan"),
                autoHideDelay: 5000,
                appendToast: false,
                variant: "danger",
                solid: true,
              },
            );
            this.$refs.barcodereader.wrongjob();
            return;
          }
          if (ProductHelper.isProductStepCompleted(product, this.currentStep)) {
            this.$bvToast.toast(
              product.name + " " + this.$t("general.alreadyscanned"),
              {
                title: this.$t("general.wrongscan"),
                autoHideDelay: 5000,
                appendToast: false,
                variant: "danger",
                solid: true,
              },
            );
            this.$refs.barcodereader.wrongjob();
            return;
          }
        }
      }

      if (this.alertMode) {
        this.openAlertFromChild(product);
      } else {
        this.completeProduct(productIdPublics).then(() => {
          if (product) {
            this.$bvToast.toast(
              product.name + " " + this.$t("general.productscompleted"),
              {
                title: this.$t("general.goodscan"),
                autoHideDelay: 5000,
                appendToast: false,
                variant: "success",
                solid: true,
              },
            );
          } else {
            this.$bvToast.toast(this.$t("general.productscompleted"), {
              title: this.$t("general.goodscan"),
              autoHideDelay: 5000,
              appendToast: false,
              variant: "success",
              solid: true,
            });
          }
          this.$refs.barcodereader.goodscan();
        });
      }
    },
    wrongWorkOrderMessage(scannedItems) {
      const h = this.$createElement;
      const workOrderIds = [
        ...new Set(scannedItems.map((x) => x.workOrderIdPublic).filter(Boolean)),
      ];
      const links = workOrderIds.map((workOrderId) => {
        const name =
          this.queryResult.availableWorkOrders.find(
            (w) => w.idPublic == workOrderId,
          )?.name ||
          this.queryResult.products.find(
            (p) =>
              scannedItems.some(
                (x) =>
                  x.workOrderIdPublic == workOrderId && x.idPublic == p.idPublic,
              ),
          )?.workOrderName;
        return h(
          "b-link",
          {
            class: "d-block font-weight-bold mt-1",
            props: {
              to: {
                name: "workOrderProducts",
                params: { workOrderPublicId: workOrderId },
                query: this.routePackingId
                  ? { packagingPublicId: this.routePackingId }
                  : {},
              },
            },
          },
          [
            h("i", { class: "mdi mdi-briefcase-outline mr-1" }),
            this.$t("general.seeprofile") + (name ? ": " + name : ""),
          ],
        );
      });
      return h("div", [this.$t("general.wrongworkorder"), ...links]);
    },
    openAlertFromChild(product) {
      this.$refs.alertModal
        .openAlert({
          product: product,
          reasons: this.reasons,
          reason:
            this.reasons.find((r) => r.idPublic === product.reasonIdPublic) ||
            this.reasons[0],
          problemNote: product.problemNote || "",
          workOrderName: this.workOrderFilter.name,
        })
        .then(
          async (result) => {
            result.stepIdPublic = this.currentStepIdPublic;
            const apiResp = await ApiService.sendAlert(result);
            if (apiResp.data.success) {
              product.isBackOrder = result.IsBackOrder;
              product.isDamaged = result.IsDamaged;
              product.reasonIdPublic = result.ReasonIdPublic;
              product.problemNote = result.ProblemNote;
              product.reasonName =
                this.reasons.find((r) => r.idPublic === product.reasonIdPublic)
                  ?.name || "";
            }
          },
          () => {},
        );
    },
    openFilesModal() {
      if (this.queryResult.workOrderInfo) {
        this.$refs.filesModal.openEventDialog(this.queryResult.workOrderInfo);
      }
    },
  },
  created() {
    this.$store
      .dispatch(
        "userconfig/getUserConfig",
        this.$msal.msalInstance.getAllAccounts()[0].localAccountId,
      )
      .then((res) => {
        if (res.defaultStep !== null) {
          const match = this.steps.find((x) => x.idPublic == res.defaultStep);
          if (match) this.currentStep = match;
        }
      });
    this.getData();
  },
};
</script>
<template>
  <Layout content-padding>
    <BarCodeReader @scan="scan" ref="barcodereader" />
    <div class="shell">
      <!-- Job bar -->
      <b-card
        v-if="loaded"
        class="m-0 shadow-none packing-header"
        body-class="jobbar d-flex align-items-center p-2 justify-content-between"
      >
        <div
          class="d-flex align-items-center"
          :class="{ 'flex-row-reverse': $route.name === 'packingProducts' }"
          style="gap: 0.7rem; min-width: 0; "
        >
          <div
            v-if="$route.name === 'workOrderProducts'"
            class="d-inline align-items-center fixed-entity"
            role="button"
            :title="$t('general.default')"
            @click="$router.push({ name: 'default' })"
          >
            <i :class="['mdi', 'mr-1', 'mdi-briefcase-outline']"></i>
            {{ workOrderFilter.name }}
          </div>
          <multiselect
            v-else
            class="w-auto pr-3"
            v-model="workOrderFilter"
            :options="workOrderOptions"
            :show-labels="false"
            :allow-empty="false"
            :multiple="false"
            :searchable="false"
            label="name"
            track-by="idPublic"
            :disabled="$route.name === 'workOrderProducts'"
          >
            <template #singleLabel="{ option }">
              <i :class="['mdi', 'mr-1', optionIconClass(option)]"></i>
              {{ option.name }}
            </template>
            <template #option="{ option }">
              <i :class="['mdi', 'mr-1', optionIconClass(option)]"></i>
              {{ option.name }}
            </template>
            <template #caret>
              <i
                class="mdi mdi-chevron-down position-absolute"
                style="top: 0; right: 0"
              ></i>
            </template>
          </multiselect>
          <i class="mdi mdi-chevron-right"></i>
          <div
            v-if="$route.name === 'packingProducts'"
            class="d-inline align-items-center fixed-entity"
            role="button"
            :title="$t('general.default')"
            @click="$router.push({ name: 'default' })"
          >
            <i :class="['mdi', 'mr-1', 'mdi-package-variant-closed']"></i>
            {{ packingFilter.name }}
          </div>
          <multiselect
            v-else
            class="w-auto pr-3"
            v-model="packingFilter"
            :options="packingOptions"
            :show-labels="false"
            :allow-empty="false"
            :multiple="false"
            :searchable="false"
            label="name"
            track-by="idPublic"
            :disabled="$route.name === 'packingProducts'"
          >
            <template #singleLabel="{ option }">
              <i :class="['mdi', 'mr-1', 'mdi-package-variant-closed']"></i>
              {{ option.name }}
            </template>
            <template #option="{ option }">
              <i :class="['mdi', 'mr-1', 'mdi-package-variant-closed']"></i>
              {{ option.name }}
            </template>
            <template #caret v-if="$route.name !== 'packingProducts'">
              <i
                class="mdi mdi-chevron-down position-absolute"
                style="top: 0; right: 0"
              ></i>
            </template>
          </multiselect>
        </div>
        <b-dropdown
          class="p-0"
          menu-class="p-0"
          toggle-class="badge"
          size="sm"
          right
          :text="currentStep ? currentStep.name : $t('general.status')"
          :variant="alertMode ? 'warning' : 'success'"
        >
          <template #button-content>
            <span>{{
              currentStep ? currentStep.name : $t("general.status")
            }}</span>
            <i
              v-if="currentStep && isStepCompleted(currentStep)"
              class="mdi mdi-check ml-1"
            ></i>
          </template>
          <b-dropdown-item v-if="!steps.length" disabled>
            {{ $t("general.nostepavailable") }}
          </b-dropdown-item>
          <b-dropdown-item
            v-for="(step, index) in steps"
            :key="step.idPublic"
            @click="selectStep(step)"
            :active="!!currentStep && currentStep.idPublic === step.idPublic"
            active-class="bg-primary"
            :class="{
              'border rounded-top': index == 0,
              'border rounded-bottom': index == steps.length - 1,
            }"
          >
            {{ step.name }}
            <b-badge
              v-if="isStepCompleted(step)"
              pill
              variant="success"
              class=" p-1 float-right"
            >
              <i class="mdi mdi-check"></i>
            </b-badge>
          </b-dropdown-item>
        </b-dropdown>
      </b-card>

      <!-- Tabs (nav rendered at the bottom via `end`) -->
      <b-tabs
        v-if="loaded"
        v-model="tabIndex"
        end
        class="flex-grow-1 d-flex flex-column d-flex justify-content-center"
        style="min-height: 0"
        nav-class="tabnav-bs"
        content-class="tab-content flex-grow-1 d-flex flex-column"
        active-nav-item-class="tabnav-active"
        no-fade
      >
        <b-tab
          v-if="workOrderFilter.idPublic"
          class="flex-grow-1"
          key="jobinfo"
        >
          <template #title>
            <i class="mdi mdi-information-outline"></i>
            <span>{{ $t("general.jobinfo") }}</span>
          </template>
          <JobInfoTab
            class="px-2"
            :nb-b-o="nbBO"
            :nb-damaged="nbDamaged"
            :nb-product="nbProduct"
            :nb-detached="nbDetached"
            :available-packagings="queryResult.availablePackagings"
            :packing-filter="packingFilter"
            :work-order-info="queryResult.workOrderInfo"
            @select-packing="selectPacking"
          />
        </b-tab>

        <b-tab class="flex-grow-1" key="progression">
          <template #title>
            <i class="mdi mdi-chart-donut"></i>
            <span>{{ $t("general.progress") }}</span>
          </template>
          <ProgressionTab
            :nb-b-o="nbBO"
            :nb-damaged="nbDamaged"
            :percent-completed="percentCompleted"
            :nb-product-completed="nbProductCompleted"
            :nb-product="nbProduct"
            :nb-detached-completed="nbDetachedCompleted"
            :nb-detached="nbDetached"
            :alert-mode.sync="alertMode"
          />
        </b-tab>

        <b-tab class="flex-grow-1" key="items">
          <template #title>
            <i class="mdi mdi-format-list-checks"></i>
            <span>{{ $t("general.items") }}</span>
          </template>
          <ItemsTab
            :products="queryResult.products"
            :current-step="currentStep"
            :steps="steps"
            :available-packagings="queryResult.availablePackagings"
            :packing-filter="packingFilter"
            :nb-alerts="nbAlerts"
            :alert-mode="alertMode"
            :show-files-button="showFilesButton"
            :can-undo-complete="canUndoComplete"
            @select-step="selectStep"
            @select-packing="selectPacking"
            @confirm-completed="confirmCompleted"
            @undo-completed="undoCompleted"
            @open-alert="openAlertFromChild"
            @toggle-alert-mode="toggleAlertMode"
            @open-files="openFilesModal"
          />
        </b-tab>
        <b-tab v-if="workOrderFilter.idPublic" class="flex-grow-1" key="notes">
          <template #title>
            <i class="mdi mdi-message-outline"></i>
            <span>{{ $t("general.notes") }}</span>
          </template>
          <NotesTab
            :work-order-id-public="
              workOrderFilter ? workOrderFilter.idPublic : null
            "
            :work-order-step-id-public="currentWorkOrderStepIdPublic"
            :work-order-steps="queryResult.workOrderSteps"
          />
        </b-tab>

        <ActionButtons
          class="mt-2"
          :can-check-all="userConfig.canCheckAll"
          :can-complete-step="userConfig.canCompleteStep"
          :all-product-completed="allProductCompleted"
          :current-work-order-step-completed="currentWorkOrderStepCompleted"
          @check-all="checkAllProducts"
          @complete-step="completeWorkOrderStep"
        />
      </b-tabs>

      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        color="#f1b44c"
        :width="70"
        :height="70"
        loader="dots"
        :is-full-page="false"
      >
      </loading>
    </div>

    <AlertModal ref="alertModal" />
    <ConfirmCompletedModal ref="confirmModal" />
    <SignatureModal ref="signatureModal" />
    <FilesModal ref="filesModal" />
  </Layout>
</template>
<style scoped>
@media (min-width: 768px) {
  .packing-header,
  .packing-header ::v-deep .multiselect__single {
    font-size: 1.1rem !important;
  }
}

::v-deep .dropdown-item.active, ::v-deep .dropdown-item:active {
    color: #fff;
    background-color: #556ee6;
    border-color: #556ee6;
}
::v-deep .tab-pane {
  min-height: 0;
}
.shell {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
}

/* Job bar */
.jobbar {
  gap: 0.7rem;
}
.fixed-entity {
  cursor: pointer;
  transition: opacity 0.15s;
}
.fixed-entity:hover {
  opacity: 0.75;
  text-decoration: underline;
}
.jobbar-select {
  flex: 1;
  min-width: 0;
  font-weight: 600;
}
.jobbar ::v-deep .multiselect__tags {
  min-height: 0 !important;
  border: none !important;
  padding: 0 0.25rem 0 0 !important;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
::v-deep .multiselect {
  min-height: 0 !important;
  min-width: 0 !important;
}
::v-deep .multiselect__content-wrapper {
  width: auto !important;
}
::v-deep .multiselect__select::before {
  display: none !important;
}

::v-deep .multiselect__single,
.fixed-entity {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.jobbar-select ::v-deep .multiselect__select::before {
  border-top-color: white;
}
.camera {
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  font-size: 18px;
}
.step-badge {
  background: #34c38f;
  color: white;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
/* Bottom tab nav styling (b-tabs with end placement) */
.shell ::v-deep .tab-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}
.shell ::v-deep .tabnav-bs {
  border-top: 1px solid #e0e5ea;
  border-bottom: none !important;
  background: white;
  display: flex;
  padding: 5px 0;
  flex-shrink: 0;
}
.shell ::v-deep .tabnav-bs .nav-item {
  flex: 1;
  text-align: center;
}
.shell ::v-deep .tabnav-bs .nav-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  border: none !important;
  background: none !important;
  color: #7a8a99 !important;
  padding: 3px 2px;
  position: relative;
  border-radius: 0;
}
.shell ::v-deep .tabnav-bs .nav-link i {
  font-size: 20px;
}
.shell ::v-deep .tabnav-bs .nav-link span:not(.tdot) {
  font-size: 12px;
  font-weight: 500;
}
.shell ::v-deep .tabnav-bs .nav-link.tabnav-active {
  font-weight: 700;
  color: #1c2e3e !important;
}
.shell ::v-deep .tabnav-bs .nav-link.tabnav-active::after {
  content: "";
  position: absolute;
  bottom: -3px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #f1b44c;
}

@media (max-width: 500px) {
  .shell {
    max-width: 100%;
    border-radius: 0;
  }
}
</style>
